import React from "react";
import PropTypes from "prop-types";
import ImagePromptLibraryService from "../../services/imagePromptLibraryService";
import { approveJob } from "../../../common/server_utils.es6";
import { VISIBLE_IMAGE_STYLE_PROVIDER_OPTIONS } from "./imageStyleProviderOptions";
import i18n from "i18next";
import "./ImageStyleLibrary.scss";

export default class ImageStyleLibrary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      styleLibrary: [],
      loading: true,
      error: null,
      selectedStyle: null,
      customPrompt: "",
      provider: "gemini-nano",
      orientation: "square",
      quality: "hd",
      isGenerating: false,
      generatedImageUrl: "",
      showAllStyles: false,
    };

    this.loadStyleLibrary = this.loadStyleLibrary.bind(this);
    this.handleStyleSelect = this.handleStyleSelect.bind(this);
    this.handleCustomPromptChange = this.handleCustomPromptChange.bind(this);
    this.handleProviderChange = this.handleProviderChange.bind(this);
    this.handleOrientationChange = this.handleOrientationChange.bind(this);
    this.handleQualityChange = this.handleQualityChange.bind(this);
    this.generateImage = this.generateImage.bind(this);
    this.addImageToCanvas = this.addImageToCanvas.bind(this);
    this.regenerateImage = this.regenerateImage.bind(this);
  }

  componentDidMount() {
    this.loadStyleLibrary();
  }

  async loadStyleLibrary() {
    try {
      this.setState({ loading: true, error: null });
      const data = await ImagePromptLibraryService.getImagePromptLibrary({
        limit: 20,
        offset: 0,
        orderBy: "createdAt",
        orderDirection: "DESC",
      });
      this.setState({
        styleLibrary: data.libraries || data.items || data || [],
        loading: false,
      });
    } catch (error) {
      console.error("Error loading style library:", error);
      this.setState({
        error: i18n.t("FileModal.APIProviders.ImageStyles.FailedToLoadStyles"),
        loading: false,
      });
    }
  }

  handleStyleSelect(style) {
    this.setState({
      selectedStyle: style,
    });
  }

  handleCustomPromptChange(event) {
    this.setState({ customPrompt: event.target.value });
  }

  handleProviderChange(event) {
    this.setState({ provider: event.target.value });
  }

  handleOrientationChange(event) {
    this.setState({ orientation: event.target.value });
  }

  handleQualityChange(event) {
    this.setState({ quality: event.target.value });
  }

  generateImage() {
    const { selectedStyle, customPrompt, provider, orientation, quality } = this.state;

    if (!selectedStyle && !customPrompt.trim()) {
      this.props.onServerModal &&
        this.props.onServerModal(
          i18n.t("FileModal.APIProviders.ImageStyles.PromptRequiredTitle"),
          i18n.t("FileModal.APIProviders.ImageStyles.PleaseEnterPrompt"),
          false
        );
      return;
    }

    this.setState({ isGenerating: true });

    const params = {
      prompt: customPrompt.trim() || (selectedStyle ? selectedStyle.name || "generate" : ""),
      provider,
      orientation,
      imageCount: 1,
      quality,
      ...(selectedStyle && { imagePromptLibraryId: selectedStyle.id }),
    };

    ImagePromptLibraryService.generateImageWithStyle(
      params,
      (isSuccess, errorStr, fileUrl, _errData, jobId) => {
        this.setState({ isGenerating: false });

        if (isSuccess && fileUrl) {
          this.setState({ generatedImageUrl: fileUrl, generatedJobId: jobId || null });
        } else {
          this.props.onServerModal &&
            this.props.onServerModal(
              i18n.t("FileModal.APIProviders.error"),
              i18n.t("FileModal.APIProviders.ImageStyles.FailedToGenerate") +
                ": " +
                (errorStr || "Unknown error"),
              false
            );
        }
      }
    );
  }

  regenerateImage() {
    this.generateImage();
  }

  addImageToCanvas() {
    const { generatedImageUrl, generatedJobId, customPrompt } = this.state;

    if (generatedImageUrl && this.props.onElementSelected) {
      const filename = `AI-${customPrompt.substring(0, 30)}`;
      this.props.onElementSelected(filename, generatedImageUrl, "image", `img_${Date.now()}`);
      // Sahneye eklendi → widget'taki "Onay Bekliyor" kartını kapat.
      approveJob(generatedJobId);
      this.setState({
        generatedImageUrl: "",
        generatedJobId: null,
        customPrompt: "",
        selectedStyle: null,
      });

      if (this.props.onServerModal) {
        this.props.onServerModal(
          i18n.t("FileModal.APIProviders.uploaded"),
          i18n.t("FileModal.APIProviders.ImageStyles.ImageAdded"),
          true
        );
      }
    }
  }

  render() {
    const {
      styleLibrary,
      loading,
      error,
      selectedStyle,
      customPrompt,
      provider,
      orientation,
      quality,
      isGenerating,
      generatedImageUrl,
      showAllStyles,
    } = this.state;

    const visibleStyles = showAllStyles ? styleLibrary : styleLibrary.slice(0, 3);

    return (
      <div className="image-style-library">
        {/* Error State */}
        {error && (
          <div className="error-section">
            <i className="fa fa-exclamation-circle error-icon"></i>
            <p className="error-message">{error}</p>
          </div>
        )}

        {/* Style Library Section */}
        {!error && (
          <div className="style-library-section">
            <h5 className="section-title">
              {i18n.t("FileModal.APIProviders.ImageStyles.StyleTemplates")}
            </h5>
            <div className="styles-grid-container">
              {loading ? (
                <div className="styles-grid">
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p className="message">
                      {i18n.t("FileModal.APIProviders.ImageStyles.LoadingStyles")}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="styles-grid">
                    {visibleStyles.map((style, index) => (
                      <div
                        key={style.id || index}
                        className={`style-card ${selectedStyle?.id === style.id ? "selected" : ""}`}
                        onClick={() => this.handleStyleSelect(style)}
                      >
                        <div className="card-body">
                          <div className="image-container">
                            {style.sampleImageUrl || style.thumbnail ? (
                              <img
                                src={style.sampleImageUrl || style.thumbnail}
                                className="thumbnail"
                                alt={style.name || style.promptText}
                              />
                            ) : (
                              <div className="placeholder-icon">
                                <i className="fa fa-image icon"></i>
                              </div>
                            )}
                            {style.category && (
                              <div className="category-badge">{style.category}</div>
                            )}
                          </div>
                          <div className="info">
                            <div className="name">
                              {style.name ||
                                i18n.t("FileModal.APIProviders.ImageStyles.StyleTemplate")}
                            </div>
                            <div className="description">
                              {style.description ||
                                (style.promptText
                                  ? style.promptText.substring(0, 50) + "..."
                                  : i18n.t("FileModal.APIProviders.ImageStyles.NoDescription"))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {styleLibrary.length > 2 && (
                    <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => this.setState({ showAllStyles: !showAllStyles })}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#7c3aed",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        {showAllStyles ? "Daha az göster" : "Devamını gör"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Form Section */}
        {!error && (
          <div className="form-section">
            <div className="form-row">
              <div className="label-col">
                <h5 className="label">
                  {i18n.t("FileModal.APIProviders.ImageStyles.CustomizePrompt")}
                </h5>
              </div>
              <div className="input-col">
                <textarea
                  className="textarea"
                  value={customPrompt}
                  onChange={this.handleCustomPromptChange}
                  placeholder={i18n.t("FileModal.APIProviders.ImageStyles.PromptPlaceholder")}
                  rows={5}
                />
                <small className="help-text">
                  {i18n.t("FileModal.APIProviders.ImageStyles.PromptHint")}
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="label-col">
                <h5 className="label">{i18n.t("FileModal.APIProviders.ImageStyles.AIProvider")}</h5>
              </div>
              <div className="input-col">
                <select className="select" value={provider} onChange={this.handleProviderChange}>
                  {VISIBLE_IMAGE_STYLE_PROVIDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="label-col">
                <h5 className="label">
                  {i18n.t("FileModal.APIProviders.ImageStyles.Orientation")}
                </h5>
              </div>
              <div className="input-col">
                <select
                  className="select"
                  value={orientation}
                  onChange={this.handleOrientationChange}
                >
                  <option value="square">
                    {i18n.t("FileModal.APIProviders.ImageStyles.Square")}
                  </option>
                  <option value="landscape">
                    {i18n.t("FileModal.APIProviders.ImageStyles.Landscape")}
                  </option>
                  <option value="portrait">
                    {i18n.t("FileModal.APIProviders.ImageStyles.Portrait")}
                  </option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="label-col">
                <h5 className="label">{i18n.t("FileModal.APIProviders.ImageStyles.Quality")}</h5>
              </div>
              <div className="input-col">
                <select className="select" value={quality} onChange={this.handleQualityChange}>
                  <option value="hd">
                    {i18n.t("FileModal.APIProviders.ImageStyles.HDQuality")}
                  </option>
                  <option value="standard">
                    {i18n.t("FileModal.APIProviders.ImageStyles.Standard")}
                  </option>
                </select>
              </div>
            </div>

            <div className="form-row submit-row">
              <div className="label-col"></div>
              <div className="input-col">
                <button
                  className="submit-button"
                  onClick={this.generateImage}
                  disabled={isGenerating || (!selectedStyle && !customPrompt.trim())}
                >
                  {isGenerating && <div className="spinner" role="status" aria-hidden="true"></div>}
                  <span>
                    {isGenerating
                      ? i18n.t("FileModal.APIProviders.ImageStyles.Generating")
                      : i18n.t("FileModal.APIProviders.ImageStyles.GenerateImage")}
                  </span>
                </button>
                <div className={`status-message ${isGenerating ? "" : "hidden"}`}>
                  {i18n.t("FileModal.APIProviders.AIAvatars.PleaseWait")}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Result Section */}
        {generatedImageUrl && (
          <div className="result-section">
            <h5 className="section-title">
              {i18n.t("FileModal.APIProviders.ImageStyles.GeneratedImage")}
            </h5>
            <img src={generatedImageUrl} alt="Generated" className="generated-image" />
            <div className="action-buttons">
              <button className="add-button" onClick={this.addImageToCanvas}>
                <i className="fa fa-plus icon"></i>
                {i18n.t("FileModal.APIProviders.ImageStyles.AddToCanvas")}
              </button>
              <button
                className="regenerate-button"
                onClick={this.regenerateImage}
                disabled={isGenerating}
              >
                <i className="fa fa-refresh icon"></i>
                {i18n.t("FileModal.APIProviders.ImageStyles.Regenerate")}
              </button>
            </div>
            <div className="success-message">
              <div className="title">
                <i className="fa fa-check-circle icon"></i>
                {i18n.t("TheImageYouCreatedHasBeenAddedToMyFiles")}
              </div>
              <div className="description">
                {i18n.t(
                  "YouCanAccessTheImagesYouHavePreviouslyCreatedHereOrDeleteTheImagesYouDoNotUse"
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

ImageStyleLibrary.propTypes = {
  onElementSelected: PropTypes.func,
  onServerModal: PropTypes.func,
};
